import { useMemo } from 'react';
import { t } from '@lingui/macro';
import {
    Cloud as CloudIcon,
    CloudCircle as CloudCircleIcon,
    CompareArrows as CompareArrowsIcon,
    DeviceHub as DeviceHubIcon,
    Language as LanguageIcon,
    Notifications as NotificationsIcon,
    Phone as PhoneIcon,
    Public as PublicIcon,
    Room as RoomIcon,
    Router as RouterIcon,
    SettingsEthernet as SettingsEthernetIcon,
    Storage as StorageIcon,
    WifiTethering as WifiTetheringIcon,
    ImportExport as ImportExportIcon,
    Schedule as ScheduleIcon,
} from '@material-ui/icons';

import { useGroupBusinessServices, useGroupPages, useGroupSites, useUser } from '../../../stores/SessionStore';
import {
    accessPointsPageId,
    agentsPageId,
    alertsPageId,
    businessServicesOverviewPageId,
    businessServicesPageId,
    hypervisorsPageId,
    interfacesPageId,
    netTestNowPageId,
    voipTestNowPageId,
    networkDevicesPageId,
    radiosPageId,
    reportsPageId,
    serversPageId,
    sitesOverviewPageId,
    sitesPageId,
    testsPageId,
    virtualMachinesPageId,
    wirelessControllersPageId,
    webTestNowPageId,
} from '../../pages';
import { NPoint2K, NetworkDevice, VmwareHypervisor, VmwareVm } from '../../icon';
import { SessionStorageUtilities } from '../../../utilities/StorageUtilities';
import { usePlmId } from '../../../utilities/PgmUtilities';

export const usePlmMainMenuPages = () => {
    const [visiblePages] = useGroupPages();

    return useMemo(() => {
        return {
            sitesOverview: {
                title: t`Sites Overview`,
                path: sitesOverviewPageId,
                icon: <LanguageIcon />,
                viewable: visiblePages.includes(sitesOverviewPageId),
            },
            sites: {
                title: t`Sites`,
                path: sitesPageId,
                icon: <RoomIcon />,
                viewable: visiblePages.includes(sitesPageId),
            },
            businessServicesOverview: {
                title: t`Business Services Overview`,
                path: businessServicesOverviewPageId,
                icon: <CloudCircleIcon />,
                viewable: visiblePages.includes(businessServicesOverviewPageId),
            },
            businessServices: {
                title: t`Business Services`,
                path: businessServicesPageId,
                icon: <CloudIcon />,
                viewable: visiblePages.includes(businessServicesPageId),
            },
            alerts: {
                title: t`Alerts`,
                path: alertsPageId,
                icon: <NotificationsIcon />,
                viewable: visiblePages.includes(alertsPageId),
            },
            agents: {
                title: t`nPoints`,
                path: agentsPageId,
                icon: <NPoint2K />,
                viewable: visiblePages.includes(agentsPageId),
            },
            tests: {
                title: t`Service Tests`,
                path: testsPageId,
                icon: <CompareArrowsIcon />,
                viewable: visiblePages.includes(testsPageId),
            },
            webTestNow: {
                title: t`Web Test Now`,
                path: webTestNowPageId,
                icon: <PublicIcon />,
                viewable: visiblePages.includes(webTestNowPageId),
            },
            voipTestNow: {
                title: t`VoIP Test Now`,
                path: voipTestNowPageId,
                icon: <PhoneIcon />,
                viewable: visiblePages.includes(voipTestNowPageId),
            },
            netTestNow: {
                title: t`Bandwidth Test Now`,
                path: netTestNowPageId,
                icon: <ImportExportIcon />,
                viewable: visiblePages.includes(netTestNowPageId),
            },
            servers: {
                title: t`Servers`,
                path: serversPageId,
                icon: <StorageIcon />,
                viewable: visiblePages.includes(serversPageId),
            },
            networkDevices: {
                title: t`Network Devices`,
                path: networkDevicesPageId,
                icon: <NetworkDevice />,
                viewable: visiblePages.includes(networkDevicesPageId),
            },
            interfaces: {
                title: t`Interfaces`,
                path: interfacesPageId,
                icon: <SettingsEthernetIcon />,
                viewable: visiblePages.includes(interfacesPageId),
            },
            wirelessControllers: {
                title: t`Wireless Controllers`,
                path: wirelessControllersPageId,
                icon: <DeviceHubIcon />,
                viewable: visiblePages.includes(wirelessControllersPageId),
            },
            accessPoints: {
                title: t`Access Points`,
                path: accessPointsPageId,
                icon: <RouterIcon />,
                viewable: visiblePages.includes(accessPointsPageId),
            },
            radios: {
                title: t`Radios`,
                path: radiosPageId,
                icon: <WifiTetheringIcon />,
                viewable: visiblePages.includes(radiosPageId),
            },
            hypervisors: {
                title: t`Hypervisors`,
                path: hypervisorsPageId,
                icon: <VmwareHypervisor />,
                viewable: visiblePages.includes(hypervisorsPageId),
            },
            virtualMachines: {
                title: t`Virtual Machines`,
                path: virtualMachinesPageId,
                icon: <VmwareVm />,
                viewable: visiblePages.includes(virtualMachinesPageId),
            },
            reports: {
                title: t`Scheduled Reports`,
                path: reportsPageId,
                icon: <ScheduleIcon />,
                viewable: visiblePages.includes(reportsPageId),
            },
        };
    }, [visiblePages]);
};

export const useShouldResetMenuHistory = () => {
    const [user] = useUser();
    const plmId = usePlmId();
    const [sites] = useGroupSites();
    const [businessServices] = useGroupBusinessServices();

    return useMemo(() => {
        const allowedBsStorageKey = `user-${user.id}${plmId ? `-plm-${plmId}` : ``}-allowed-business-services`;
        const allowedSitesStorageKey = `user-${user.id}${plmId ? `-plm-${plmId}` : ``}-allowed-sites`;

        const previousAllowedSites = SessionStorageUtilities.getJSONValue(allowedSitesStorageKey) || [];
        const previousAllowedBs = SessionStorageUtilities.getJSONValue(allowedBsStorageKey) || [];
        const shouldResetHistory =
            sites?.length !== previousAllowedSites?.length ||
            !sites?.every((site) => previousAllowedSites?.includes(site)) ||
            businessServices?.length !== previousAllowedBs?.length ||
            !businessServices?.every((bs) => previousAllowedBs?.includes(bs));

        if (shouldResetHistory) {
            SessionStorageUtilities.setJSONValue(allowedSitesStorageKey, sites);
            SessionStorageUtilities.setJSONValue(allowedBsStorageKey, businessServices);
        }

        return shouldResetHistory;
    }, [sites, businessServices, plmId, user.id]);
};
